import {
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Box,
    Alert
} from "@mui/material";
import Button from "@mui/material/Button";
import MicIcon from '@mui/icons-material/Mic';
import ClearIcon from '@mui/icons-material/Clear';
import { useEffect, useRef, useState } from "react";
import useAudioRecorder from "../hooks/audio/useAudioRecorder";
import useApiHook from "../hooks/useApiHook";
import RecorderControls from "./RecorderControls";
import RecordingTimer from "./_atoms/RecordingTimer";
import Typography from "@mui/material/Typography";
import UploadProgress from "./_atoms/UploadProgress";
import ErrorIcon from "@mui/icons-material/Error";
import DownloadIcon from '@mui/icons-material/Download';
import { usePackageModal } from "../store/modal-context";
import { useUser } from "../store/user-context";
import HiddenContentSwitch from "./_atoms/inputs/HiddenContentSwitch";

const AudioRecorderDialog = (props) => {
    const {
        isRecording,
        isPaused,
        recordingTime,
        audioURL,
        error,
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        resetRecording,
        getAudioFile,
        cleanup
    } = useAudioRecorder();

    const { response, makeRequest, loading, uploadProgress, error: requestError } = useApiHook();
    const { onFileUpload, onClose } = props;
    const transcribeRequestedRef = useRef(false);
    const packageModalWasOpenRef = useRef(false);
    const [uploadLimitError, setUploadLimitError] = useState(false);
    const packageModalCtx = usePackageModal();
    const userCtx = useUser();
    const hiddenContentSwitchRef = useRef(null);
    const [hiddenContentSwitchVal, setHiddenContentSwitchVal] = useState(false);

    const resetState = () => {
        transcribeRequestedRef.current = false;
        setUploadLimitError(false);
        resetRecording();
    };

    useEffect(() => {
        if (props.open) {
            if (userCtx.hasReachLimit()) return setUploadLimitError(true);

            resetState();
        }
    }, [props.open]);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, []);

    useEffect(() => {
        if (!props.open || !audioURL || !uploadLimitError) return;

        const refreshUsage = () => {
            userCtx.fetchUsage();
        };

        window.addEventListener('focus', refreshUsage);
        document.addEventListener('visibilitychange', refreshUsage);

        return () => {
            window.removeEventListener('focus', refreshUsage);
            document.removeEventListener('visibilitychange', refreshUsage);
        };
    }, [audioURL, props.open, uploadLimitError, userCtx]);

    useEffect(() => {
        if (!props.open) {
            packageModalWasOpenRef.current = false;
            return;
        }

        if (packageModalCtx.isOpen) {
            packageModalWasOpenRef.current = true;
            return;
        }

        if (packageModalWasOpenRef.current) {
            packageModalWasOpenRef.current = false;
            userCtx.fetchUsage();
        }
    }, [packageModalCtx.isOpen, props.open, userCtx]);

    useEffect(() => {
        if (response && !transcribeRequestedRef.current) {
            if (response.state === 'success') {
                if (response.data && response.data.fileId) {
                    transcribeRequestedRef.current = true;
                    (async () => {
                        await makeRequest('/file/transcribe', 'post', {
                            fileId: response.data.fileId,
                            folderId: props.currentFolderId,
                            hideSensitiveContent: hiddenContentSwitchVal
                        });
                        resetRecording();
                        onFileUpload();
                        onClose();
                        userCtx.fetchUsage();
                    })();
                }
            }
        }
        if (requestError) {
            if (requestError.error.code === 400) {
                setUploadLimitError(true);
            }
        }
    }, [response, requestError]);

    useEffect(() => {
        if (!uploadLimitError || !audioURL) return;
        if (userCtx.canUploadSeconds(recordingTime)) {
            setUploadLimitError(false);
        }
    }, [audioURL, recordingTime, uploadLimitError, userCtx]);

    const handleClose = () => {
        if (isRecording) {
            stopRecording();
        }
        resetRecording();
        onClose();
    };

    const handleSave = () => {
        const audioFile = getAudioFile();
        setUploadLimitError(false);
        if (audioFile) {
            if (!userCtx.canUploadSeconds(recordingTime)) {
                setUploadLimitError(true);
                return;
            }

            setHiddenContentSwitchVal(hiddenContentSwitchRef.current);
            makeRequest('/file', 'post', { audioFile });
            userCtx.fetchUsage();
        }
    };

    const handleDelete = () => {
        setUploadLimitError(false);
        resetRecording();
    };

    const onBuyCreditsBtnClick = () => {
        packageModalCtx.openModal();
    };

    const downloadRecordingBtnClickHandler = () => {
        const audioFile = getAudioFile();
        if (!audioFile) return;

        const downloadUrl = URL.createObjectURL(audioFile);
        const link = document.createElement('a');

        link.href = downloadUrl;
        link.download = audioFile.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
    };

    return (
        <Dialog
            open={props.open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth>
            <Stack direction="row">
                <DialogTitle sx={{ flexGrow: 1 }}>
                    {loading ?
                        "Caricamento in corso..." :
                        uploadLimitError && !audioURL ? "Minuti non sufficienti per questo audio" :
                        audioURL ? "Anteprima Registrazione" :
                            "Registra Audio"}
                </DialogTitle>
                <Button variant="text" color="error" size="small" onClick={handleClose}>
                    <ClearIcon />
                </Button>
            </Stack>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {(uploadLimitError && !audioURL) && <Stack alignItems='center' justifyContent="center">
                    <ErrorIcon sx={{ fontSize: '4rem' }} color={'error'} />
                    <Typography mt={2} color={"error"} variant='h4'>Minuti non sufficienti per questa trascrizione</Typography>
                    <Typography textAlign='center' variant='h6'>Per trascrivere questo audio servono piu minuti di quelli disponibili nel tuo account.</Typography>
                    <Button sx={{ mt: 4 }} variant="contained" size='large' onClick={onBuyCreditsBtnClick}>Acquista nuovi crediti</Button>
                </Stack>}

                {!uploadLimitError && !loading && !audioURL && (
                    <>
                        <DialogContentText textAlign="center">
                            {isRecording ?
                                "Registrazione in corso..." :
                                "Clicca su 'Inizia Registrazione' per registrare un nuovo file audio da trascrivere."
                            }
                        </DialogContentText>
                        {props.currentFolderId && (
                            <Typography mt={2} textAlign="center" variant="body2" sx={{ opacity: 0.65 }}>
                                Il file verra caricato nella cartella <strong>{props.currentFolderName}</strong>.
                            </Typography>
                        )}

                        <Box textAlign="center" my={4}>
                            <MicIcon
                                sx={{
                                    fontSize: 120,
                                    color: isRecording && !isPaused ? 'error.main' : 'action.disabled',
                                    animation: isRecording && !isPaused ? 'pulse 1.5s infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%': { opacity: 1, transform: 'scale(1)' },
                                        '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                                        '100%': { opacity: 1, transform: 'scale(1)' }
                                    }
                                }}
                            />
                        </Box>

                        {isRecording && (
                            <Box mb={2}>
                                <Typography
                                    variant="body2"
                                    textAlign="center"
                                    color="error"
                                    fontWeight="bold">
                                    {isPaused ? 'IN PAUSA' : 'REC'}
                                </Typography>
                            </Box>
                        )}

                        {(isRecording || recordingTime > 0) && (
                            <RecordingTimer seconds={recordingTime} />
                        )}

                        <RecorderControls
                            isRecording={isRecording}
                            isPaused={isPaused}
                            hasRecording={false}
                            onStart={startRecording}
                            onPause={pauseRecording}
                            onResume={resumeRecording}
                            onStop={stopRecording}
                        />
                    </>
                )}

                {!uploadLimitError && !loading && audioURL && (
                    <>
                        <DialogContentText textAlign="center" mb={2}>
                            Riascolta la tua registrazione prima di salvarla.
                        </DialogContentText>

                        <Box textAlign="center" my={3}>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Durata: {Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}
                            </Typography>
                            <audio
                                controls
                                src={audioURL}
                                style={{ width: '100%', maxWidth: 400 }}
                            />
                        </Box>
                        <HiddenContentSwitch ref={hiddenContentSwitchRef} />

                        <RecorderControls
                            isRecording={false}
                            isPaused={false}
                            hasRecording={true}
                            onSave={handleSave}
                            onDelete={handleDelete}
                        />
                    </>
                )}

                {(uploadLimitError && !loading && audioURL) && (
                    <>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            Non hai abbastanza minuti per caricare questa registrazione. L'audio e ancora disponibile qui sotto:
                            puoi acquistare nuovi crediti e riprovare senza registrare di nuovo.
                        </Alert>

                        <DialogContentText textAlign="center" mb={2}>
                            La registrazione non e stata caricata. Puoi riascoltarla e salvarla piu tardi.
                        </DialogContentText>
                        <Typography textAlign="center" color="warning.main" fontWeight="bold" mb={2}>
                            Per sicurezza, scarica la registrazione sul dispositivo per non rischiare di perderla.
                        </Typography>

                        <Box textAlign="center" my={3}>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Durata: {Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, '0')}
                            </Typography>
                            <audio
                                controls
                                src={audioURL}
                                style={{ width: '100%', maxWidth: 400 }}
                            />
                        </Box>
                        <HiddenContentSwitch ref={hiddenContentSwitchRef} />

                        <Stack direction="row" spacing={2} justifyContent="center" my={3}>
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadRecordingBtnClickHandler}>
                                Scarica audio
                            </Button>
                            <Button variant="contained" onClick={onBuyCreditsBtnClick}>
                                Acquista nuovi crediti
                            </Button>
                        </Stack>

                        <RecorderControls
                            isRecording={false}
                            isPaused={false}
                            hasRecording={true}
                            onSave={handleSave}
                            onDelete={handleDelete}
                        />
                    </>
                )}

                {!uploadLimitError && loading && (
                    <Stack alignItems="center" spacing={2} my={4}>
                        <UploadProgress uploadPercent={uploadProgress} />
                        <Typography variant="body2" color="text.secondary">
                            Caricamento file in corso... {Math.round(uploadProgress)}%
                        </Typography>
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AudioRecorderDialog;
